import { Link, NavLink } from "react-router-dom";
import { Search, Bell, Menu } from "lucide-react";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SocialLinks } from "./SocialLinks";
import { DemandeVerificationDialog } from "./DemandeVerificationDialog";
import { useRubriques } from "@/hooks/useRubriques";
import { useCategories } from "@/hooks/useCategories";
import { useParametres } from "@/hooks/useParametres";
import type { RubriqueDto } from "@/types/rubrique.types";
import { estCategorieEducation } from "@/lib/categories";
import logoParDefaut from "../../assets/logo_Ramba.png";

export function Header() {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const { data: rubriquesData } = useRubriques();
  const { data: categoriesData } = useCategories();
  const { data: parametres } = useParametres();
  const siteTitre = parametres?.["site.titre"] || "RAMBA NEWS";
  const siteSlogan = parametres?.["site.slogan"] || t("brand.newsroom");
  const logo = parametres?.["site.logo"] || logoParDefaut;

  // ✅ Rubriques depuis l'API — seules les rubriques actives, triées par ordre
  const rubriques: RubriqueDto[] = (Array.isArray(rubriquesData) ? rubriquesData : [])
    .filter((r) => r.actif)
    .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));

  // "Éducation aux médias" est une catégorie à part entière, gérée en admin —
  // ne jamais coder son slug en dur (ça la confond avec "Décryptages", qui est
  // un tout autre contenu). On résout son vrai slug dynamiquement.
  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const categorieEducation = categories.find((c: any) => estCategorieEducation(c));
  const educationHref = categorieEducation ? `/categories/${categorieEducation.slug}` : "/categories/education-aux-medias";

  // "Direct" (nav.live) est retiré du menu principal, mais la route /live et
  // son contenu restent accessibles par lien direct (décision produit validée).
  const mainNav = [
    { to: "/", label: t("nav.home") },
    { to: "/factcheck", label: t("nav.factcheck") },
    { to: educationHref, label: t("nav.explainers") },
    { to: "/about", label: t("nav.about") },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Top strip — flex-wrap à deux niveaux : le bloc de droite passe sur sa
            propre ligne s'il ne tient pas à côté des réseaux sociaux (iPhone
            SE/11), ET ses propres éléments (langue / alertes / vérification)
            peuvent eux aussi se replier plutôt que déborder hors de l'écran. */}
        <div className="flex flex-wrap items-center gap-y-1.5 py-2 text-xs text-muted-foreground border-b border-rule">
          <SocialLinks />
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            <LanguageSwitcher />
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 rounded-md bg-alert px-2.5 py-1 text-xs font-medium text-alert-foreground hover:opacity-90"
            >
              <Bell className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("cta.followAlerts")}</span>
            </Link>
            <DemandeVerificationDialog />
          </div>
        </div>

        {/* Brand row */}
        <div className="flex items-center justify-between py-4">
          <button
            className="md:hidden -ml-2 p-2 text-foreground"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <Link to="/" className="flex items-center gap-2">
            <img
              src={logo}
              alt={`${siteTitre} Logo`}
              className="h-10 w-10 object-contain rounded-sm"
            />
            <div className="leading-tight">
              <div className="text-base font-semibold tracking-tight">{siteTitre}</div>
              <div className="hidden sm:block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {siteSlogan}
              </div>
            </div>
          </Link>

          <Link
            to="/search"
            aria-label={t("nav.search")}
            className="rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <Search className="h-5 w-5" />
          </Link>
        </div>

        {/* Main nav */}
        <nav className="hidden md:flex items-center gap-6 overflow-x-auto py-2 text-sm">
          {mainNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-foreground font-semibold"
                    : "text-foreground/80 hover:text-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <span className="h-4 w-px bg-rule" />
          {/* ✅ Rubriques API avec slug réel */}
          {rubriques.slice(0, 6).map((r) => (
            <Link
              key={r.slug}
              to={`/rubrique/${r.slug}`}
              className="whitespace-nowrap text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              {locale === "fr" ? r.nom : (r.nomEn || r.nom)}
            </Link>
          ))}
        </nav>

        {/* Mobile menu */}
        {open && (
          <nav className="md:hidden flex flex-col gap-1 pb-3">
            {mainNav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded px-2 py-2 text-sm hover:bg-accent"
              >
                {n.label}
              </Link>
            ))}
            {/* ✅ Rubriques aussi dans le menu mobile */}
            <div className="mt-2 border-t border-border pt-2">
              {rubriques.slice(0, 6).map((r) => (
                <Link
                  key={r.slug}
                  to={`/rubrique/${r.slug}`}
                  onClick={() => setOpen(false)}
                  className="block rounded px-2 py-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-accent"
                >
                  {locale === "fr" ? r.nom : (r.nomEn || r.nom)}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}